import { type QueryClient, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  type AiModelSummary,
  listUserFacingTierDefaultsOptions,
  type ModelTierDefaultResponse,
  modelsListModelsOptions,
} from "@/api";
import { storedDefaultModelId } from "@/features/account/storedSettings";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { ROUTER_TIER } from "./reasoning";

/**
 * One selectable row in the chat model picker. `id` is what gets sent to the
 * backend on session create: either a real model id or a `tier:<name>`
 * reference (the backend resolves tier references itself, see
 * backend/src/config/models.py:resolve_model_name).
 */
export interface ChatModelOption {
  id: string;
  label: string;
  description?: string;
  /** True for the virtual tier options (Default Fast, etc.) that sit above the divider. */
  isTier: boolean;
}

const TIER_PREFIX = "tier:";
/** "Choose for me" — the router tier, preferred default when present. */
const CHOOSE_FOR_ME = ROUTER_TIER;

/**
 * The chat model dropdown's rows: user-facing tier defaults on top, then the enabled, non-deprecated chat
 * models (so audio/image/research models like Whisper never appear).
 */
export function chatModelOptions(
  models: readonly AiModelSummary[] | undefined,
  tiers: readonly ModelTierDefaultResponse[] | undefined,
) {
  const chatModels = (models ?? []).filter((m) => (m.modelType ?? "chat") === "chat" && m.isEnabled && !m.isDeprecated);
  // A tier only counts if the admin has actually pointed it at a model.
  const tierDefaults = (tiers ?? []).filter((td) => td.userFacing !== false && td.modelId);
  const options: ChatModelOption[] = [
    ...tierDefaults.map((td) => ({
      id: `${TIER_PREFIX}${td.tier}`,
      label: td.label,
      description: td.description ?? td.modelName ?? undefined,
      isTier: true,
    })),
    ...chatModels.map((m) => ({
      id: m.id,
      label: m.displayName ?? m.name,
      description: m.description ?? undefined,
      isTier: false,
    })),
  ];
  return { chatModels, tierDefaults, options };
}

/** The user's saved default when still offered, else the router tier ("Choose for me"), else the first chat model. */
export function defaultChatModelId(options: readonly ChatModelOption[], preferredId: string | null = null) {
  if (preferredId && options.some((o) => o.id === preferredId)) return preferredId;
  return options.some((o) => o.id === CHOOSE_FOR_ME) ? CHOOSE_FOR_ME : (options[0]?.id ?? null);
}

/** The model a new chat starts with, outside a component (loads the lists through the query cache). */
export async function fetchDefaultChatModelId(queryClient: QueryClient): Promise<string | null> {
  const [models, tiers] = await Promise.all([
    queryClient.fetchQuery(modelsListModelsOptions()),
    queryClient.fetchQuery(listUserFacingTierDefaultsOptions()),
  ]);
  return defaultChatModelId(chatModelOptions(models.data, tiers.data.defaults).options);
}

/** Builds the chat model dropdown (chatModelOptions) and owns the selected value, defaulting as defaultChatModelId. */
export function useChatModels() {
  const preferredId = storedDefaultModelId(useCurrentUser().data?.user.settings);
  const modelsQuery = useQuery({ ...modelsListModelsOptions(), select: (r) => r.data });
  const tiersQuery = useQuery({
    ...listUserFacingTierDefaultsOptions(),
    select: (r) => r.data.defaults ?? [],
  });

  const { chatModels, tierDefaults, options } = useMemo(
    () => chatModelOptions(modelsQuery.data, tiersQuery.data),
    [modelsQuery.data, tiersQuery.data],
  );
  const defaultId = useMemo(() => defaultChatModelId(options, preferredId), [options, preferredId]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Once options load, fall back to the computed default until the user picks.
  const resolvedId = selectedId && options.some((o) => o.id === selectedId) ? selectedId : defaultId;

  return {
    options,
    /** Enabled chat models and configured tiers, for resolving what a model id answers with. */
    models: chatModels,
    tierDefaults,
    tierCount: tierDefaults.length,
    selectedId: resolvedId,
    select: setSelectedId,
    selectedLabel: options.find((o) => o.id === resolvedId)?.label ?? "",
    isLoading: modelsQuery.isPending || tiersQuery.isPending,
    /** Both lists loaded, so `options` is the complete set of valid ids. */
    isLoaded: modelsQuery.isSuccess && tiersQuery.isSuccess,
    noModel: modelsQuery.isSuccess && options.length === 0,
  };
}
